import React from "react";
import { X, Link2, CalendarDays } from "lucide-react";
import api from "@/lib/api";

/** "Fri 18 Dec, 6:00 PM MST · Colorado Springs, CO" — always in the EVENT's timezone. */
const eventSummary = (preview) => {
  const parts = [];
  if (preview.event_start) {
    const d = new Date(preview.event_start);
    if (!Number.isNaN(d.getTime())) {
      const opts = {
        weekday: "short", day: "numeric", month: "short",
        hour: "numeric", minute: "2-digit",
      };
      // Without an explicit zone the browser prints the reader's local time, which for a
      // Denver event read from London lands on the wrong day entirely.
      if (preview.event_tz) { opts.timeZone = preview.event_tz; opts.timeZoneName = "short"; }
      try {
        parts.push(d.toLocaleString(undefined, opts));
      } catch {
        parts.push(d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short",
                                                 hour: "numeric", minute: "2-digit" }));
      }
    }
  }
  if (preview.event_place) parts.push(preview.event_place);
  return parts.join(" · ");
};

/**
 * Unfurled link card. The metadata and image are snapshotted server-side when the post
 * is created, so nothing here calls the source site at render time.
 */
export const LinkPreviewCard = ({ preview, onRemove, testId = "link-preview" }) => {
  if (!preview || !preview.title) return null;
  const host = preview.site_name || (() => {
    try { return new URL(preview.url).hostname.replace(/^www\./, ""); } catch { return preview.url; }
  })();
  const eventLine = eventSummary(preview);

  const body = (
    <>
      {preview.image_url && (
        <div className="aspect-[1.91/1] max-h-52 w-full overflow-hidden bg-muted">
          <img src={preview.image_url} alt="" loading="lazy"
               className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]" />
        </div>
      )}
      <div className="space-y-1 px-3.5 py-3">
        <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
          <Link2 className="h-3 w-3 shrink-0" />
          <span className="truncate">{host}</span>
        </p>
        <p className="line-clamp-2 text-sm font-[640] leading-snug text-foreground" data-testid={`${testId}-title`}>
          {preview.title}
        </p>
        {eventLine && (
          <p className="flex items-center gap-1.5 text-xs font-[540] text-primary" data-testid={`${testId}-event`}>
            <CalendarDays className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{eventLine}</span>
          </p>
        )}
        {preview.description && (
          <p className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">{preview.description}</p>
        )}
      </div>
    </>
  );

  return (
    <div className="group relative mt-3 overflow-hidden border border-border/80 bg-card transition-colors hover:border-primary/40"
         data-testid={testId}>
      {onRemove ? (
        <>
          {body}
          <button type="button" onClick={onRemove} aria-label="Remove link preview"
                  className="absolute right-2 top-2 grid h-7 w-7 place-items-center bg-background/90 text-muted-foreground shadow-sm transition-colors hover:text-foreground"
                  data-testid={`${testId}-remove`}>
            <X className="h-4 w-4" />
          </button>
        </>
      ) : (
        <a href={preview.url} target="_blank" rel="noopener noreferrer nofollow" className="block"
           data-testid={`${testId}-link`}>
          {body}
        </a>
      )}
    </div>
  );
};

/** First http(s) link in a rich-text body — href first, then bare text. */
export const firstUrlIn = (html) => {
  if (!html) return null;
  const href = html.match(/<a[^>]+href=["'](https?:\/\/[^"']+)["']/i);
  const raw = href ? href[1] : (html.replace(/<[^>]+>/g, " ").match(/https?:\/\/[^\s<>"']+/i) || [])[0];
  return raw ? raw.replace(/[.,;:!?)"']+$/, "") : null;
};

/**
 * Composer unfurl: watches the body, fetches the card for the first link, and remembers
 * what the author dismissed so it doesn't reappear as they keep typing.
 */
export const useLinkPreview = (body, { enabled = true } = {}) => {
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const dismissed = React.useRef(new Set());
  const url = enabled ? firstUrlIn(body) : null;

  React.useEffect(() => {
    if (!url || dismissed.current.has(url)) { setPreview(null); return; }
    let live = true;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/chatter/link-preview", { url });
        if (live) setPreview(data.preview ? { ...data.preview, url } : null);
      } catch { if (live) setPreview(null); }
      finally { if (live) setLoading(false); }
    }, 700);
    return () => { live = false; clearTimeout(t); setLoading(false); clearTimeout(t); };
  }, [url]);

  const remove = () => { if (url) dismissed.current.add(url); setPreview(null); };
  const reset = () => { dismissed.current = new Set(); setPreview(null); };
  return { preview, loading, remove, reset, previewUrl: preview ? url : null };
};
